import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { GraduationCap, TrendingDown, ArrowLeft, Clock, Mail, Phone } from 'lucide-react';

const ComingSoon = () => {
  const navigate = useNavigate();
  const { type } = useParams();

  const plannerInfo = {
    'education-planner': {
      title: "Children's Future Education Fund Planner",
      icon: GraduationCap,
      description: 'Secure your child\'s educational future with strategic investment planning',
      features: [
        'Calculate education costs for different courses and universities',
        'Factor in education-specific inflation rates (typically 10-12%)',
        'Plan for domestic and international education options',
        'Optimize investment timeline based on child\'s age',
        'Compare different education savings schemes',
        'Generate step-by-step funding strategy'
      ],
      gradient: 'from-green-700 to-orange-600'
    },
    'swp-planner': {
      title: 'Wealth Preservation Planner',
      icon: TrendingDown,
      description: 'Optimize your Systematic Withdrawal Plan for sustainable income',
      features: [
        'Calculate optimal withdrawal rates for longevity',
        'Factor in market volatility and sequence risk',
        'Tax-efficient withdrawal strategies',
        'Balance growth and income requirements',
        'Plan for inflation-adjusted income',
        'Stress-test your withdrawal plan'
      ],
      gradient: 'from-orange-600 to-yellow-600'
    }
  };

  const info = plannerInfo[type] || plannerInfo['education-planner'];
  const IconComponent = info.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-green-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-between items-center mb-6">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex items-center gap-2 border-2 border-blue-800 text-blue-800 hover:bg-blue-800 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Button>
            
            <div className="flex-1 flex flex-col items-center gap-4">
              <a href="https://www.aumonline.in" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/5j61poys_Logo.jpg" 
                  alt="AUMONLINE Logo" 
                  className="h-20 md:h-24 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                />
              </a>
              <a href="https://www.njwealth.in" target="_blank" rel="noopener noreferrer">
                <img 
                  src="https://customer-assets.emergentagent.com/job_retire-planner-2/artifacts/0iyf1b7c_image.png" 
                  alt="NJ WEALTH Logo" 
                  className="h-14 md:h-16 object-contain hover:opacity-80 transition-opacity duration-200 cursor-pointer"
                />
              </a>
            </div>
            
            <div className="w-32"></div> {/* Spacer for balance */}
          </div>
        </div>

        {/* Coming Soon Card */}
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-xl border-0">
            <CardHeader className={`bg-gradient-to-r ${info.gradient} text-white`}>
              <CardTitle className="text-3xl md:text-4xl text-center flex items-center justify-center gap-4">
                <IconComponent className="w-12 h-12" />
                {info.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-12">
              {/* Coming Soon Badge */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-3 bg-yellow-100 text-yellow-800 px-6 py-3 rounded-full text-lg font-semibold">
                  <Clock className="w-6 h-6" />
                  Coming Soon
                </div>
              </div>

              {/* Description */}
              <div className="text-center mb-10">
                <p className="text-xl text-gray-700 mb-6 leading-relaxed">
                  {info.description}
                </p>
                <p className="text-lg text-gray-600">
                  We're working hard to bring you this comprehensive financial planning tool. 
                  Stay tuned for advanced features that will help you make informed decisions about your financial future.
                </p>
              </div>

              {/* Planned Features */}
              <div className="mb-10">
                <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Planned Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {info.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg">
                      <div className="w-3 h-3 rounded-full bg-green-500 mt-2 flex-shrink-0"></div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Get Notified Section */}
              <Card className="bg-gradient-to-r from-blue-800 to-green-700 text-white mb-8">
                <CardContent className="p-8 text-center">
                  <h4 className="text-2xl font-bold mb-4">Get Notified When It's Ready!</h4>
                  <p className="text-lg opacity-90 mb-6">
                    Be the first to know when this planner launches. We'll send you an email notification.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <div className="flex items-center gap-3 text-lg">
                      <Mail className="w-6 h-6" />
                      <span>info@aumonline.com</span>
                    </div>
                    <div className="flex items-center gap-3 text-lg">
                      <Phone className="w-6 h-6" />
                      <span>+91 9845 123 456</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Navigation Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/')}
                  className="bg-gradient-to-r from-blue-800 to-green-700 hover:from-blue-900 hover:to-green-800 text-white px-8 py-3 text-lg font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-3"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back to Home
                </Button>
                
                <Button
                  onClick={() => navigate('/retirement-planner')}
                  className="bg-gradient-to-r from-green-700 to-orange-600 hover:from-green-800 hover:to-orange-700 text-white px-8 py-3 text-lg font-semibold rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                >
                  Try Retirement Planner
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ComingSoon;